/**
 * useCustomerActivityCreate - Criação de andamentos vinculados ao CLIENTE
 *
 * Ponto único no frontend para criar atividades de cliente. Chama a RPC
 * `create_customer_activity` (SECURITY DEFINER), que valida tenant, ownership
 * do cliente, whitelist de tipos e vincula automaticamente ao negócio mais
 * recente do cliente quando existir.
 *
 * Substitui o INSERT direto em `crm_deal_activities` que era feito pelo
 * CustomerActivityFormSheet.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-09 v1.1.0 - Suporte ao modo CONTATO (contact_id): timeline
 *          compartilhada entre contato e cliente
 * RELEASE: 2026-08-09 v1.0.0 - Versão inicial
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('CustomerActivityCreate');

/** Entrada da criação de andamento do cliente */
export interface CreateCustomerActivityInput {
  /** ID do cliente (opcional no modo Contato) */
  customer_id?: string | null;
  /** v1.1.0: ID do contato (modo Contato, sem cliente vinculado) */
  contact_id?: string | null;
  activity_type: string;
  title: string;
  description?: string | null;
  /** Data/hora local (input datetime-local) — convertida para UTC aqui */
  scheduled_at?: string | null;
  /** RELEASE: 2026-09-12 - pessoas mencionadas já na criação do andamento */
  mentioned_user_ids?: string[] | null;
}

/**
 * Hook de criação de andamentos do cliente.
 */
export function useCustomerActivityCreate(
  customerId: string | null,
  /** v1.1.0: contato usado quando não há cliente vinculado */
  contactId: string | null = null
) {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();

  const createActivity = useMutation({
    mutationFn: async (input: CreateCustomerActivityInput): Promise<string> => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      const { data, error } = await supabase.rpc('create_customer_activity' as never, {
        p_customer_id: input.customer_id ?? null,
        p_contact_id: input.contact_id ?? contactId ?? null,
        p_activity_type: input.activity_type,
        p_title: input.title,
        p_description: input.description || null,
        // Converte horário local para UTC antes de salvar
        p_scheduled_at: input.scheduled_at
          ? new Date(input.scheduled_at).toISOString()
          : null,
        p_performed_by: user?.id || null,
        p_source: 'manual',
        // RELEASE: 2026-09-12 - menções criadas atomicamente pela RPC
        p_mentioned_user_ids: input.mentioned_user_ids?.length
          ? input.mentioned_user_ids
          : null,
        p_tenant_hint: currentTenant.id,
      } as never);

      if (error) {
        logger.error('Erro ao criar andamento do cliente:', error.message);
        throw error;
      }

      return data as unknown as string;
    },
    onSuccess: () => {
      // Invalida as listagens que exibem a timeline do cliente
      queryClient.invalidateQueries({ queryKey: ['customer-activities', customerId] });
      queryClient.invalidateQueries({ queryKey: ['customer-activities-paginated', customerId] });
      // v1.1.0: invalida também a timeline no modo Contato
      queryClient.invalidateQueries({ queryKey: ['customer-activities-paginated', customerId, contactId] });
      queryClient.invalidateQueries({ queryKey: ['customer-activities', contactId] });
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities'] });
    },
  });

  return { createActivity };
}
